import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import moment from 'moment';
import { db } from '../lib/firebase';
import '../styles/Transfer.css';
import Loader from '../components/Loader';
import progress from '../images/progress.png';
import ChartInfo from '../components/ChartInfo';
import Verification from '../components/Verification';
import { sendTransactionEmail } from '../lib/Email';


const Transfer = ({ user }) => {

  // State variables for form fields and other data
  const [bankName, setBankName] = useState('');
  const [beneficiary, setBeneficiary] = useState('');
  const [beneficiaryAccount, setBeneficiaryAccount] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState(moment().format('YYYY-MM-DD'));
  const [account, setAccount] = useState('Savings');
  const [totalCredits, setTotalCredits] = useState(0);
  const [totalDebits, setTotalDebits] = useState(0);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [userBalance, setUserBalance] = useState(0);
  const [accountBalances, setAccountBalances] = useState({});
  const [loading, setLoading] = useState(true);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [userGroup, setUserGroup] = useState(null);
  const [userDP, setUserDP] = useState(null);
  const [userFullName, setUserFullName] = useState('');
  const [userAccountNumber, setUserAccountNumber] = useState('');
  const report = '[Pending] ' + description;
  const [showVerificationBox, setShowVerificationBox] = useState();
  const [userAmount, setUserAmount] = useState();
  const [userBankName, setUserBankName] = useState();
  const [userBeneficiary, setUserBeneficiary] = useState();
  const [userBeneficiaryAccount, setUserBeneficiaryAccount] = useState();





  useEffect(() => {
    if (user && user.uid) {
      // Fetch user data including transactions and group
      const fetchUserData = async () => {
        try {
          const userDoc = await getDoc(doc(db, 'greenwtb', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const fetchedTransactions = userData.transactions || [];
            const accountBalances = {};

            // Fetch user info
            const userAccountNumber = userData.accountNumber || '************';
            const userFullName = userData.fullName || 'No name';
            const userGroup = userData.group || 'No group available';
            const userDP = userData.profilePictureUrl || progress;

            // Calculate total credits, debits, and balances for each account type
            const credits = fetchedTransactions.reduce((total, txn) => txn.type === 'credit' ? total + txn.amount : total, 0);
            const debits = fetchedTransactions.reduce((total, txn) => txn.type === 'debit' ? total + txn.amount : total, 0);
            const totalBalance = credits - debits;

            fetchedTransactions.forEach((txn) => {
              if (!accountBalances[txn.account]) {
                accountBalances[txn.account] = 0;
              }
              accountBalances[txn.account] += txn.type === 'credit' ? txn.amount : -txn.amount;
            });

            // Set all the fetched data to state
            setTotalCredits(credits);
            setTotalDebits(debits);
            setTotalTransactions(fetchedTransactions.length);
            setUserBalance(totalBalance);
            setAccountBalances(accountBalances);
            setTransactions(fetchedTransactions);
            setUserGroup(userGroup);
            setUserDP(userDP);
            setUserFullName(userFullName);
            setUserAccountNumber(userAccountNumber);

          }
        } catch (error) {
          console.error('Error fetching user data: ', error);
        } finally {
          setLoading(false);
        }
      };

      fetchUserData();
    } else {
      setLoading(false);
    }
  }, [user]);


  // Check if the selected account has insufficient balance
  const isInsufficientBalance = parseFloat(amount) > (accountBalances[account] || 0);


  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormSubmitting(true);
    setSuccessMessage('');

    if (!bankName || !beneficiary || !beneficiaryAccount || !amount || !description || !account) {
      setFormSubmitting(false);
      return;
    }

    const parsedAmount = parseFloat(amount);
    if (accountBalances[account] <= 0 || parsedAmount > accountBalances[account]) {
      setFormSubmitting(false);
      return;
    }

    try {
      // Fetch the logged-in user's data
      const userDocRef = doc(db, 'greenwtb', user.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        console.error('User document not found.');
        return;
      }

      const userData = userDoc.data();
      const fullName = userData.fullName;
      const accountNumber = userData.accountNumber;

      const transaction = {
        bankName,
        beneficiary,
        beneficiaryAccount,
        amount: parsedAmount,
        description: userGroup === 'deactivated' ? report : description,
        date: moment(date).format('MMMM D, YYYY'),
        type: 'debit',
        account,
      };

      // Perform the transaction in Firestore
      await updateDoc(userDocRef, {
        transactions: arrayUnion(transaction),
      });

      // Send confirmation email with correct fullName
      await sendTransactionEmail(user.email, fullName, accountNumber, transaction);

      // Update balance locally
      setUserBalance(userBalance - parsedAmount);
      setAccountBalances(prevBalances => ({
        ...prevBalances,
        [account]: prevBalances[account] - parsedAmount
      }));

      // Reset form fields
      setBankName('');
      setBeneficiary('');
      setBeneficiaryAccount('');
      setAmount('');
      setDescription('');
      setDate(moment().format('YYYY-MM-DD'));
      setAccount('Savings');
      setUserAmount(`$${amount}`);
      setUserBeneficiary(`${beneficiary}`);
      setUserBeneficiaryAccount(`${beneficiaryAccount}`);
      setUserBankName(`${bankName}`);
      setSuccessMessage(`The transfer of $${amount} was sent to ${beneficiary}.`);

      // Ensure the transaction is completed before updating the user's group
      if (userGroup === 'deactivated') {
        await updateDoc(userDocRef, {
          group: 'cancelled',
        });

        // Update local state
        setUserGroup('cancelled');
      }

      // Show verification box if user is not activated
      if (userGroup !== 'activated') {
        setShowVerificationBox(true);
      }

    } catch (error) {
      console.error('Error adding transaction: ', error);
    } finally {
      setFormSubmitting(false);
    }
  };




  if (loading) {
    return <div><Loader /></div>;
  }

  return (
    <div className="add-transaction" id='transfer'>
      {/* Display total credits, debits, and transactions */}
      <div className="summary-boxed">
        <div className="summaryBoxes">
          Credits: ${totalCredits.toFixed(2)}
        </div>
        <div className="summaryBoxes">
          Debits: ${totalDebits.toFixed(2)}
        </div>
        <div className="summaryBoxes">
          Total Transactions: {totalTransactions}
        </div>
      </div>

      {/* Display chart for transactions */}
      <div>
        <ChartInfo transactions={transactions} />
      </div>


      {/* Transaction Form */}
      <form onSubmit={handleSubmit}>
        <div className='form-cover'>
          {/* Beneficiary */}
          <div>
            <label className='transaction-head'>Beneficiary:</label>
            <input
              type="text"
              value={beneficiary}
              placeholder='John Doe'
              className='input-field'
              onChange={(e) => setBeneficiary(e.target.value)}
              required
            />
          </div>

          {/* Beneficiary Account */}
          <div>
            <label className='transaction-head'>Beneficiary Account:</label>
            <input
              type="text"
              value={beneficiaryAccount}
              className="input-field"
              placeholder="0123456789"
              onChange={(e) => {
                const value = e.target.value;
                if (/^[0-9]*$/.test(value)) { // Matches only numbers
                  setBeneficiaryAccount(value);
                }
              }}
              required
            />

          </div>
        </div>

        <div className='form-cover'>
          {/* Bank Name */}
          <div>
            <label className='transaction-head'>Bank Name:</label>
            <input
              type="text"
              className='input-field'
              value={bankName}
              placeholder='Bank of America'
              onChange={(e) => setBankName(e.target.value)}
              required
            />
          </div>

          {/* Amount */}
          <div>
            <label className='transaction-head'>Amount:</label>
            <input
              type="number"
              value={amount}
              className='input-field'
              placeholder='example: 100'
              onChange={(e) => setAmount(e.target.value)}
              required
              min="0.01"
              step="0.01"
            />
          </div>
        </div>

        <div className='form-cover'>
          {/* Description */}
          <div>
            <label className='transaction-head'>Description:</label>
            <input
              type="text"
              className='input-field'
              value={description}
              placeholder='Payment for Electricity'
              onChange={(e) => setDescription(e.target.value)}
              required
            />
          </div>

          {/* Select Account Type */}
          {/* <div>
            <label className='transaction-head'>Account Type:</label>
            <select
              className='input-field'
              value={account}
              onChange={(e) => setAccount(e.target.value)}
              required
            >
              <option value="Savings">Savings (${accountBalances.Savings?.toFixed(2) || 0.00})</option>
              <option value="Investments">Investments (${accountBalances.Investments?.toFixed(2) || 0.00})</option>
              <option value="Loans">Loans (${accountBalances.Loans?.toFixed(2) || 0.00})</option>
            </select>
          </div> */}
        </div>

        {/* Submit button */}
        {userGroup === 'cancelled' ? (
          <div className="cancelled-message">
            <p>Transactions have been disabled for this account.</p>
          </div>
        ) : (
          <button type="submit" className="contact-support-button" disabled={formSubmitting || isInsufficientBalance}>
            {formSubmitting ? 'Processing...' : 'Transfer'}
          </button>
        )}

      </form>


      {/* Success and error messages */}
      {successMessage && (
        <div>
          <div>

            <div>
              <div>
                <div>
                  {userGroup ? (
                    userGroup !== 'activated' ? (
                      <>
                        {showVerificationBox && (
                          // show verification box if user is deactivated
                          <Verification setShowVerificationBox={setShowVerificationBox} />
                        )}
                      </>
                    ) : (
                      // show success message
                      <div className="msg">
                        <div className={`msg-box ${user.group === 'deactivated' ? 'pending' : 'success'}`}>
                          <div>
                            <span className="contact-support-button" onClick={() => setSuccessMessage('')}>&times;</span>
                          </div>
                          <h2 style={{ color: '#dedede' }}>Your transaction was successful!</h2>
                          <div className="details-container">
                            {/* Sender Details */}
                            <div className="sender-details">
                              <img src={userDP} alt="Sender" />
                              <div>
                                <h4>{userFullName}</h4>
                                <p>Account: {userAccountNumber}</p>
                                <p>Transaction Amount: {userAmount}</p>
                              </div>
                            </div>

                            {/* Connector Line with Arrow */}
                            <div className="connector-line"></div>

                            {/* Beneficiary Details */}
                            <div className="beneficiary-details">
                              <img src={progress} alt="Beneficiary" />
                              <div>
                                <h4>{userBeneficiary}</h4>
                                <p>Beneficiary Bank: {userBankName}</p>
                                <p>Account: {userBeneficiaryAccount}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>



                    )
                  ) : (
                    <>
                      <img
                        src={progress}
                        alt={user.group === 'deactivated' ? "Transaction Pending" : "Success"}
                        className="transferIMG"
                      />
                      <h4>Transaction in progress, please wait...</h4></>
                  )}

                </div>
              </div>

            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Transfer;
